import * as React from 'react';

import { useSetAtom } from 'jotai';

import { useRolesQuery } from '../_graphql/useRolesQuery';
import { useUserRoleAssignmentsQuery } from '../_graphql/useUserRoleAssignmentsQuery';

import {
  $updateUserRoleForm,
  type UserRoleAssignment,
} from '~/modules/settings/_state/userForm.state';

export function useInitUserRoleFormFromApi(userId: string | undefined) {
  const updateRoleForm = useSetAtom($updateUserRoleForm);
  const hasLoadedRef = React.useRef(false);
  const prevUserIdRef = React.useRef(userId);

  if (prevUserIdRef.current !== userId) {
    prevUserIdRef.current = userId;
    hasLoadedRef.current = false;
  }

  const { data: { roles = [] } = {}, isSuccess: isRolesSuccess } =
    useRolesQuery({
      limit: 1000,
      offset: 0,
    });

  const { data: assignmentsData, isSuccess: isAssignmentsSuccess } =
    useUserRoleAssignmentsQuery({
      userId: userId ?? '',
    });
  const rawAssignments = assignmentsData?.assignments ?? [];

  const prevAssignmentsRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!userId || roles.length === 0) return;

    const assignmentsKey = JSON.stringify(rawAssignments);
    if (assignmentsKey === prevAssignmentsRef.current) return;
    prevAssignmentsRef.current = assignmentsKey;

    const roleFacilityMap = new Map<string, string[]>();
    rawAssignments.forEach((a) => {
      const existing = roleFacilityMap.get(a.roleId) ?? [];
      existing.push(a.facilityId);
      roleFacilityMap.set(a.roleId, existing);
    });

    const initialAssignments: UserRoleAssignment[] = roles.map((role) => ({
      roleId: role.id,
      roleName: role.name,
      userCount: role.userCount,
      assignedFacilityIds: roleFacilityMap.get(role.id) ?? [],
      lastUpdatedAt: null,
      lastUpdatedBy: null,
    }));

    updateRoleForm({
      assignments: initialAssignments,
      rawAssignments: rawAssignments.map((a) => ({
        id: a.id,
        roleId: a.roleId,
        facilityId: a.facilityId,
      })),
    });
  }, [userId, roles, rawAssignments, updateRoleForm]);

  if (!!userId && isRolesSuccess && isAssignmentsSuccess) {
    hasLoadedRef.current = true;
  }

  return { hasRoleFormLoaded: hasLoadedRef.current };
}
